import pako from 'pako';
import { AudioConfig } from './AudioConfig';

/**
 * 音频录制器类，负责麦克风录音和音频数据处理
 * 使用 AudioWorklet (替代已弃用的 ScriptProcessor) 进行高性能音频处理
 */
export default class AudioRecorder extends EventTarget {
  // 事件类型常量
  static readonly FRAME_EVENT = 'frameEvent';
  static readonly POWER_EVENT = 'powerEvent';

  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private audioQueue: number[] = [];
  private isRecording: boolean = false;
  private config: AudioConfig;
  private silenceStartTime: number | null = null;

  private frameSamples: number;

  constructor(config?: AudioConfig) {
    super();
    this.config = config || new AudioConfig();
    // Calculate frame samples based on target sample rate (default 16000)
    this.frameSamples = Math.round(this.config.sampleRate / 1000 * this.config.frameMs);
  }

  /**
   * 开始录制
   */
  public async startRecording(): Promise<AudioRecorder> {
    if (this.isRecording) {
      console.warn('录音已在进行中');
      return this;
    }

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: this.config.numChannels,
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl,
        },
      });

      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AC();

      // 1. 兼容性检测
      if (!this.audioContext.audioWorklet) {
        throw new Error('您的浏览器不支持 AudioWorklet，无法进行录音。请升级浏览器或使用 HTTPS。');
      }

      // 2. 解决 iOS Safari 状态可能为 suspended 的问题
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // 加载 AudioWorklet 模块
      const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' });
      const url = URL.createObjectURL(blob);
      await this.audioContext.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'recorder-processor');

      // 处理 Worklet 发回的音频数据
      this.workletNode.port.onmessage = (event) => {
        this.handleAudioProcess(event.data);
      };

      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.audioContext.destination); // 保持管道激活

      this.isRecording = true;
      this.audioQueue = [];
      console.log('录音开始');

      return this;
    } catch (error) {
      console.error('启动录音失败:', error);
      throw error;
    }
  }

  /**
   * 停止录制
   */
  public async stopRecording(): Promise<AudioRecorder> {
    if (!this.isRecording) {
      console.warn('未在录音状态');
      return this;
    }

    this.isRecording = false;

    // 断开连接并清理节点
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode.port.onmessage = null;
      this.workletNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // 处理剩余音频队列
    if (this.audioQueue.length > 0) {
      this.processAndSendFrames(true);
    }

    console.log('录音完成');
    return this;
  }

  /**
   * 处理音频数据 (From AudioWorklet)
   */
  private handleAudioProcess(inputData: Float32Array): void {
    if (!this.isRecording) return;

    const currentRate = this.audioContext?.sampleRate || 48000;

    // 计算音量 (RMS)
    let sum = 0;
    const len = inputData.length;
    for (let i = 0; i < len; i++) {
      sum += inputData[i] * inputData[i];
    }
    const rms = Math.sqrt(sum / len);
    const powerLevel = Math.min(100, Math.round(rms* 100));


    // 发送音量事件
    this.dispatchEvent(
      new CustomEvent(AudioRecorder.POWER_EVENT, {
        detail: {
          powerLevel,
          bufferSampleRate: currentRate,
          timestamp: Date.now(),
        },
      })
    );

    // 静音检测：如果 RMS 小于阈值，直接返回，不进行处理和发送
    if (rms * 100 < this.config.silenceThreshold) {
      this.silenceStartTime ??= Date.now();
      if (this.audioQueue.length > 0 && Date.now() - this.silenceStartTime >= this.config.silenceDuration) {
        // 超过静音时长，如果有剩余数据，发送剩余帧
        console.log('静音最终帧');
        this.processAndSendFrames(true);
        this.silenceStartTime = null;
      }
      return;
    }

    // 有声音，重置静音计时
    this.silenceStartTime = null;


    // 重采样并添加到队列
    const resampled = this.resample(inputData, currentRate, this.config.sampleRate);
    for (let i = 0; i < resampled.length; i++) {
      this.audioQueue.push(resampled[i]);
    }

    // 积攒足够数据后发送帧
    while (this.audioQueue.length >= this.frameSamples) {
      this.processAndSendFrames(false);
    }
  }

  /**
   * 处理并发送音频帧
   */
  private processAndSendFrames(isFinal: boolean): void {
    let frameSamples: number[];

    if (isFinal) {
      console.log('发送最终帧', this.audioQueue.length);
      frameSamples = this.audioQueue;
      this.audioQueue = [];
    } else {
      frameSamples = this.audioQueue.slice(0, this.frameSamples);
      this.audioQueue = this.audioQueue.slice(this.frameSamples);
    }

    if (frameSamples.length === 0) return;

    const bytes = this.floatTo16LEBytes(frameSamples);
    const compressed = pako.deflate(bytes);
    const base64 = this.uint8ToBase64(compressed);

    this.dispatchEvent(
      new CustomEvent(AudioRecorder.FRAME_EVENT, {
        detail: {
          data: base64,
          is_final: isFinal,
        },
      })
    );
  }

  // --- Helpers ---

  private resample(input: Float32Array, sourceRate: number, targetRate: number): Float32Array {
    if (sourceRate === targetRate) return input;
    const ratio = sourceRate / targetRate;
    const newLen = Math.round(input.length / ratio);
    const output = new Float32Array(newLen);
    for (let i = 0; i < newLen; i++) {
      const pos = i * ratio;
      const idx = Math.floor(pos);
      const idx2 = Math.min(idx + 1, input.length - 1);
      const t = pos - idx;
      output[i] = input[idx] + (input[idx2] - input[idx]) * t;
    }
    return output;
  }

  private floatTo16LEBytes(samples: number[] | Float32Array): Uint8Array {
    const buffer = new ArrayBuffer(samples.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < samples.length; i++) {
      let s = samples[i];
      if (s > 1) s = 1;
      if (s < -1) s = -1;
      const v = s < 0 ? s * 32768 : s * 32767;
      view.setInt16(i * 2, v, true);
    }
    return new Uint8Array(buffer);
  }

  private uint8ToBase64(u8: Uint8Array): string {
    const CHUNK = 0x8000;
    let result = '';
    for (let i = 0; i < u8.length; i += CHUNK) {
      const sub = u8.subarray(i, i + CHUNK);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result += String.fromCharCode.apply(null, sub as any);
    }
    return btoa(result);
  }

  /**
   * 清理资源
   */
  private cleanup(): void {
    this.audioQueue = [];
  }

  /**
   * 获取录音状态
   */
  public get recording(): boolean {
    return this.isRecording;
  }

  /**
   * 销毁实例
   */
  public dispose(): void {
    if (this.isRecording) {
      this.stopRecording().catch(console.error);
    }
    this.cleanup();
  }
}

// AudioWorkletProcessor 代码
const WORKLET_CODE = `
class RecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.bufferSize = 4096;
    this.buffer = new Float32Array(this.bufferSize);
    this.bytesWritten = 0;
  }

  process(inputs) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const channelData = input[0];
      
      if (this.bytesWritten + channelData.length > this.bufferSize) {
        this.port.postMessage(this.buffer.slice(0, this.bytesWritten));
        this.bytesWritten = 0;
      }
      
      this.buffer.set(channelData, this.bytesWritten);
      this.bytesWritten += channelData.length;
      
      if (this.bytesWritten >= this.bufferSize) {
        this.port.postMessage(this.buffer.slice(0, this.bytesWritten));
        this.bytesWritten = 0;
      }
    }
    return true;
  }
}
registerProcessor('recorder-processor', RecorderProcessor);
`;
